import { Button } from "@beweegbaas/ui";
import { ClientOnly, createFileRoute } from "@tanstack/react-router";
import { HistoryBackLink } from "#/components/back-link";
import { ErrorBoundaryComponent } from "#/components/error-boundary";
import { Page } from "#/components/page";
import { getActivityDetail } from "#/modules/beweegactiviteitenreeks/activities.functions";
import { beweegactiviteitFields } from "#/modules/beweegactiviteitenreeks/types.beweegactiviteiten";
import { isLeerjaarValid } from "#/modules/beweegactiviteitenreeks/validation.is-leerjaar-valid";
import { useDownloadAllMediakaartenReeks } from "#/modules/mediakaarten/download-mediakaarten-reeks";
import { MediakaartWithPhotosPdfPreview } from "#/modules/mediakaarten/pdf-components/pdf.mediakaart-with-photos.previewer.client";
import { MediakaartPdfPreview } from "#/modules/mediakaarten/pdf-components/pdf.mediakaart.previewer.client";
import { buildLocaleHref } from "#/modules/util.helpers/route-href";
import type { components } from "#/schema";

type BeweegactiviteitDetailModel = components["schemas"]["BeweegactiviteitDetailModel"];

export const Route = createFileRoute(
  "/$locale/{-$schoolyear}/_authenticated/$school/activities/mediakaart-preview/$reeksId",
)({
  loader: async ({ params }) => {
    const { beweegactiviteitenreeks: reeks } = await getActivityDetail({
      data: { id: params.reeksId },
    });

    const cards: { leerjaarCombo: string; cardData: BeweegactiviteitDetailModel }[] = [];
    for (const field of beweegactiviteitFields) {
      const activiteit = reeks[field];
      if (activiteit && isLeerjaarValid(activiteit) === "valid") {
        cards.push({
          leerjaarCombo: activiteit.leerjaarCombo,
          cardData: { ...activiteit, beweegthema: reeks.beweegthema },
        });
      }
    }

    return { reeks, cards };
  },
  component: MediakaartPreviewPage,
  errorComponent: MediakaartPreviewError,
});

function MediakaartPreviewError({ error }: { error: unknown }) {
  const params = Route.useParams();
  return <ErrorBoundaryComponent error={error} params={params} />;
}

function MediakaartPreviewPage() {
  const { reeks, cards } = Route.useLoaderData();
  const { locale, schoolyear, school } = Route.useParams();
  const downloadMutation = useDownloadAllMediakaartenReeks();
  const resourceBaseUrl = import.meta.env.SSR
    ? ""
    : `${window.location.origin}${buildLocaleHref(`/api/${school}/resource`, { locale, schoolyear })}`;

  const handleDownload = () => {
    if (cards.length === 0) return;
    downloadMutation.mutate({ reeks, resourceBaseUrl });
  };

  return (
    <Page>
      <Page.Header>
        <HistoryBackLink
          fallback={{
            to: "/$locale/{-$schoolyear}/$school/activities",
            params: { locale, schoolyear, school },
          }}
          className={"max-md:self-start max-sm:mb-4"}
        />
        <Page.Title>Mediakaart preview: {reeks.name}</Page.Title>

        <Page.Header.Actions>
          <Page.Header.MainAction>
            <Button
              className="shrink-0"
              size="xl"
              isPending={downloadMutation.isPending}
              onClick={handleDownload}
            >
              <IconMaterialSymbolsDownload className="size-4" />
              Download
            </Button>
          </Page.Header.MainAction>
        </Page.Header.Actions>
      </Page.Header>
      <Page.Container>
        <Page.Body>
          {cards.length > 0 ? (
            <div className="flex flex-col gap-10">
              <div>
                <h2 className="mb-4 text-lg font-semibold">Mediakaarten</h2>
                <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
                  {cards.map((card) => (
                    <div key={card.leerjaarCombo} className="flex flex-col gap-2">
                      <ClientOnly
                        fallback={
                          <div className="bg-muted aspect-297/210 w-full animate-pulse rounded" />
                        }
                      >
                        <MediakaartPdfPreview cardData={card.cardData} />
                      </ClientOnly>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <h2 className="mb-4 text-lg font-semibold">
                  Mediakaarten met foto&apos;s (A3 portret)
                </h2>
                <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
                  {cards.map((card) => (
                    <div key={card.leerjaarCombo} className="flex flex-col gap-2">
                      <ClientOnly
                        fallback={
                          <div className="bg-muted aspect-297/420 w-full animate-pulse rounded" />
                        }
                      >
                        <MediakaartWithPhotosPdfPreview
                          cardData={card.cardData}
                          resourceBaseUrl={resourceBaseUrl}
                        />
                      </ClientOnly>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <p className="text-muted-foreground">Deze reeks heeft geen mediakaarten om te tonen.</p>
          )}
        </Page.Body>
      </Page.Container>
    </Page>
  );
}
